import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

// Must track the edge defaults in mapFixationCooldown.js, otherwise a failed
// load renders the cooldown as off while it is actually running.
const DEFAULT_CONFIG = {
  enabled: true,
  consecutiveThreshold: 3,
  playerRatioThreshold: 0.7,
  historyDepth: 10,
  lookbackDays: 30,
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

export default function MapVoteSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingKey, setSavingKey] = useState('');
  const [enabled, setEnabled] = useState(DEFAULT_CONFIG.enabled);
  const [consecutiveThreshold, setConsecutiveThreshold] = useState(DEFAULT_CONFIG.consecutiveThreshold);
  const [playerRatioPercent, setPlayerRatioPercent] = useState(Math.round(DEFAULT_CONFIG.playerRatioThreshold * 100));
  const [historyDepth, setHistoryDepth] = useState(DEFAULT_CONFIG.historyDepth);
  const [lookbackDays, setLookbackDays] = useState(DEFAULT_CONFIG.lookbackDays);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applyConfig = useCallback((cfg) => {
    setEnabled(cfg?.enabled === true);
    setConsecutiveThreshold(Number.isInteger(cfg?.consecutiveThreshold) ? cfg.consecutiveThreshold : DEFAULT_CONFIG.consecutiveThreshold);
    setPlayerRatioPercent(typeof cfg?.playerRatioThreshold === 'number' ? Math.round(cfg.playerRatioThreshold * 100) : Math.round(DEFAULT_CONFIG.playerRatioThreshold * 100));
    setHistoryDepth(Number.isInteger(cfg?.historyDepth) ? cfg.historyDepth : DEFAULT_CONFIG.historyDepth);
    setLookbackDays(Number.isInteger(cfg?.lookbackDays) ? cfg.lookbackDays : DEFAULT_CONFIG.lookbackDays);
  }, []);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/map-vote/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load map vote settings');
      if (!isMountedRef.current) return;
      applyConfig(data.mapFixationCooldown);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [applyConfig, basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (next, key) => {
    if (!userId) return;
    setSaving(true);
    setSavingKey(key || '');
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/map-vote/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapFixationCooldown: next }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save map vote settings');
      if (!isMountedRef.current) return;
      applyConfig(data.mapFixationCooldown);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      setSuccess('Map vote settings saved.');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
        setSavingKey('');
      }
    }
  }, [applyConfig, basePath, isMountedRef, userId]);

  const currentConfig = useCallback(() => ({
    enabled,
    consecutiveThreshold: Number.parseInt(consecutiveThreshold, 10) || DEFAULT_CONFIG.consecutiveThreshold,
    playerRatioThreshold: (Number.parseInt(playerRatioPercent, 10) || Math.round(DEFAULT_CONFIG.playerRatioThreshold * 100)) / 100,
    historyDepth: Number.parseInt(historyDepth, 10) || DEFAULT_CONFIG.historyDepth,
    lookbackDays: Number.parseInt(lookbackDays, 10) || DEFAULT_CONFIG.lookbackDays,
  }), [consecutiveThreshold, enabled, historyDepth, lookbackDays, playerRatioPercent]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Map Vote</Typography>
          <Typography variant="body2" color="textSecondary">
            Controls the post-accept map vote. The fixation cooldown withholds a map from the vote when the group has been playing it back to back, forcing at least one game on something else.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
          {saving ? <CircularProgress size={18} /> : null}
        </Box>

        {updatedAtMs ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Last updated: {new Date(updatedAtMs).toISOString()}
          </Typography>
        ) : null}

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}

        <Box style={{ marginTop: 24, padding: 16, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Fixation Cooldown
          </Typography>

          <Box className={classes.row}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled === true}
                  onChange={(event) => {
                    const next = { ...currentConfig(), enabled: event.target.checked };
                    setEnabled(next.enabled);
                    save(next, 'enabled');
                  }}
                  color="primary"
                  disabled={loading || saving || !userId}
                />
              }
              label="Enable map fixation cooldown"
            />
          </Box>

          <Box className={classes.row}>
            <TextField
              label="Consecutive matches"
              type="number"
              value={consecutiveThreshold}
              onChange={(event) => setConsecutiveThreshold(event.target.value)}
              onBlur={() => {
                const next = { ...currentConfig() };
                setConsecutiveThreshold(next.consecutiveThreshold);
                save(next, 'consecutiveThreshold');
              }}
              helperText="A player is fixated once this many of their most recent matches in a row were on the same map."
              inputProps={{ min: 2, max: 10, step: 1 }}
              size="small"
              variant="outlined"
              disabled={loading || saving || !userId}
            />
            <TextField
              label="Player threshold %"
              type="number"
              value={playerRatioPercent}
              onChange={(event) => setPlayerRatioPercent(event.target.value)}
              onBlur={() => {
                const next = { ...currentConfig() };
                setPlayerRatioPercent(Math.round(next.playerRatioThreshold * 100));
                save(next, 'playerRatioThreshold');
              }}
              helperText="Share of the match roster that must share the fixation before the map is withheld."
              inputProps={{ min: 10, max: 100, step: 5 }}
              size="small"
              variant="outlined"
              disabled={loading || saving || !userId}
            />
            <TextField
              label="History depth"
              type="number"
              value={historyDepth}
              onChange={(event) => setHistoryDepth(event.target.value)}
              onBlur={() => {
                const next = { ...currentConfig() };
                setHistoryDepth(next.historyDepth);
                save(next, 'historyDepth');
              }}
              helperText="How many recent finished matches are inspected per player."
              inputProps={{ min: 3, max: 50, step: 1 }}
              size="small"
              variant="outlined"
              disabled={loading || saving || !userId}
            />
            <TextField
              label="Lookback days"
              type="number"
              value={lookbackDays}
              onChange={(event) => setLookbackDays(event.target.value)}
              onBlur={() => {
                const next = { ...currentConfig() };
                setLookbackDays(next.lookbackDays);
                save(next, 'lookbackDays');
              }}
              helperText="Matches older than this are ignored when measuring the streak."
              inputProps={{ min: 1, max: 365, step: 1 }}
              size="small"
              variant="outlined"
              disabled={loading || saving || !userId}
            />
          </Box>

          <Typography className={classes.status} variant="body2" color="textSecondary">
            Current: {enabled ? 'enabled' : 'disabled'} — a map is withheld when {playerRatioPercent}% of the roster has played it in {consecutiveThreshold} straight matches (looking at up to {historyDepth} matches from the last {lookbackDays} days).
          </Typography>

          <Typography className={classes.status} variant="body2" color="textSecondary">
            A vote always keeps at least 2 maps, so the cooldown will skip a ban rather than empty the ballot.
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
}
